// A SMART ROOM's settings (door + motion sensor) — opened from SmartRoomBar.
//
//   Night            the room's night window (may cross midnight)
//   Auto-lock        lock on entering the night, unlock on leaving it
//   Sleep mode       at night, someone inside and still for N min → everything
//                    OFF except fans and AC (once per stillness)
//   Door left open   at night, open for N min → a notification
//   Ready-made       one-tap AUTOMATIONS for this room — night light, welcome
//                    light, wake-up light. They are ordinary automations (the
//                    server runs them, docs/19): edit or delete them in Automations.
//   Who hears it     the alert grid — the 🔒 column decides who hears a locked
//                    room's alarm and with which sound.
//
// Saved by the server (security.js SetRoomSmartSettings, automations.js).

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, MenuItem, Switch, TextField, Typography,
} from '@mui/material';
import { useApp } from '../../core/state/AppContext';
import { getAutomations, saveAutomation, setRoomSmartSettings } from '../../core/api/api';
import SecurityAlertsDialog from './SecurityAlertsDialog';
import AutomationsDialog from './AutomationsDialog';
import { FIT_SM } from './fitDialog';

const SLEEP_CHOICES = [0, 10, 15, 20, 30, 45, 60];
const DOOR_CHOICES = [0, 5, 10, 15, 30, 60];
const errorText = (e, fallback) => e?.response?.data?.error || e?.message || fallback;
const isFan = (t) => Number(t.Dimming) > 0 && Number(t.Output) > Number(t.On_off);
const eventOf = (sensor, word) => (sensor?.Events || []).find((e) => String(e).toLowerCase() === word) || '';

/**
 * The ready-made automations, as SaveAutomation bodies. Pure, so it is tested.
 * -> [{ Automation, Actions }]
 */
export function readyMadeAutomations(kind, {
  locationId, roomName, door, motion, light, night, useDoor = true, useMotion = true,
}) {
  const actions = [{ UniqueNumber: light.UniqueNumber, Output: Number(light.Output), Command: 'on' }];
  const base = {
    LocationID: Number(locationId), Enabled: 1, TriggerKind: 'rf', Days: 0, ModeIs: '', OnlyIfOff: 1,
  };
  const out = [];
  const add = (sensor, meaning, extra, label) => {
    if (!sensor || !meaning) return;
    out.push({
      Automation: {
        ...base, ...extra, Name: `${roomName}: ${label}`, TriggerRfDeviceId: sensor.RfDeviceId, TriggerMeaning: meaning,
      },
      Actions: actions,
    });
  };
  const from = night.from || '22:00';
  const to = night.to || '06:00';
  if (kind === 'night') {
    // Getting up at night: a short light, then off by itself.
    const extra = {
      TimeFrom: from, TimeTo: to, OccupancyIs: '', CooldownMin: 0, OffAfterMin: 2,
    };
    if (useDoor) add(door, eventOf(door, 'open'), extra, 'Night light (door)');
    if (useMotion) add(motion, eventOf(motion, 'motion'), extra, 'Night light (motion)');
  } else if (kind === 'welcome') {
    // Walking into an empty room.
    add(door, eventOf(door, 'open'), {
      TimeFrom: '', TimeTo: '', OccupancyIs: 'empty', CooldownMin: 0, OffAfterMin: 0,
    }, 'Welcome light');
  } else if (kind === 'wake') {
    // The first movement of the morning — once a day.
    add(motion, eventOf(motion, 'motion'), {
      TimeFrom: '06:00', TimeTo: '09:00', OccupancyIs: '', CooldownMin: 720, OffAfterMin: 0,
    }, 'Wake-up light');
  }
  return out;
}

const READY = [
  { kind: 'night', title: 'Night light', text: 'At night, the door opens or there is movement → the light comes on, off again after 2 min.' },
  { kind: 'welcome', title: 'Welcome light', text: 'The door opens while the room is empty → the light comes on.' },
  { kind: 'wake', title: 'Wake-up light', text: 'The first movement between 6 and 9 AM → the light comes on (once a day).' },
];

export default function SmartRoomDialog({
  open, onClose, presence, roomId, roomName, locationId, onSaved,
}) {
  const { userId } = useApp();
  const initial = presence?.settings || {};
  const [form, setForm] = useState({
    nightFrom: initial.nightFrom || '22:00',
    nightTo: initial.nightTo || '06:00',
    autoLock: !!initial.autoLock,
    sleepAfterMin: initial.sleepAfterMin || 0,
    doorOpenAlertMin: initial.doorOpenAlertMin || 0,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [catalog, setCatalog] = useState(null); // GetAutomations: sensors + targets
  const [light, setLight] = useState('');
  const [nightDoor, setNightDoor] = useState(true);
  const [nightMotion, setNightMotion] = useState(true);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);

  useEffect(() => {
    if (!open || !locationId) return undefined;
    let alive = true;
    getAutomations(locationId, userId)
      .then((res) => { if (alive) setCatalog(res?.data || { sensors: [], targets: [] }); })
      .catch(() => { if (alive) setCatalog({ sensors: [], targets: [] }); });
    return () => { alive = false; };
  }, [open, locationId, userId]);

  const inRoom = (x) => String(x.RoomID) === String(roomId);
  const door = useMemo(() => (catalog?.sensors || []).find((x) => inRoom(x) && x.RfType === 'door'), [catalog]); // eslint-disable-line react-hooks/exhaustive-deps
  const motion = useMemo(() => (catalog?.sensors || []).find((x) => inRoom(x) && x.RfType === 'motion'), [catalog]); // eslint-disable-line react-hooks/exhaustive-deps
  const lights = useMemo(() => (catalog?.targets || []).filter((t) => inRoom(t) && !isFan(t)), [catalog]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!light && lights[0]) setLight(`${lights[0].UniqueNumber}/${lights[0].Output}`); }, [lights, light]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }));

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await setRoomSmartSettings(roomId, userId, form);
      onSaved?.();
      onClose();
    } catch (e) {
      setError(errorText(e, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };

  const addReady = async (kind) => {
    const target = lights.find((t) => `${t.UniqueNumber}/${t.Output}` === light);
    if (!target) return;
    const bodies = readyMadeAutomations(kind, {
      locationId,
      roomName: roomName || 'Room',
      door,
      motion,
      light: target,
      night: { from: form.nightFrom, to: form.nightTo },
      useDoor: nightDoor,
      useMotion: nightMotion,
    });
    if (!bodies.length) {
      setError('The sensor this needs has not been set up yet (its Open / Motion code is not learned) — add it with Scan first.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      for (const b of bodies) {
        // eslint-disable-next-line no-await-in-loop
        await saveAutomation(userId, b.Automation, b.Actions);
      }
      setDone(`${READY.find((r) => r.kind === kind).title} added for ${target.OutputName || target.DeviceName}. Edit or remove it in Automations.`);
    } catch (e) {
      setError(errorText(e, 'Could not add it'));
    } finally {
      setBusy(false);
    }
  };

  const notReady = initial.available === false;

  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : onClose} {...FIT_SM}>
        <DialogTitle>{`${roomName || 'Room'} — smart room`}</DialogTitle>
        <DialogContent>
          {notReady && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The server has not been updated for these settings yet (smart_room_lock_migration.sql). The lock and the ready-made lights work; the night settings will save once it is.
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Night</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
            <TextField size="small" type="time" label="From" value={form.nightFrom} onChange={set('nightFrom')} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="time" label="To" value={form.nightTo} onChange={set('nightTo')} InputLabelProps={{ shrink: true }} />
          </Box>
          <FormControlLabel
            control={<Switch checked={form.autoLock} onChange={set('autoLock')} />}
            label="Lock the room automatically at night (unlocks itself in the morning)"
          />
          <TextField
            select size="small" fullWidth sx={{ mt: 1.5 }} label="Sleep mode"
            value={form.sleepAfterMin} onChange={set('sleepAfterMin')}
            helperText="At night, someone inside and still this long → everything off except the fan and AC."
          >
            {SLEEP_CHOICES.map((m) => <MenuItem key={m} value={m}>{m ? `After ${m} min still` : 'Off'}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" fullWidth sx={{ mt: 1.5 }} label="Door left open"
            value={form.doorOpenAlertMin} onChange={set('doorOpenAlertMin')}
            helperText="At night, the door open this long → a notification (to who is ticked on the door sensor)."
          >
            {DOOR_CHOICES.map((m) => <MenuItem key={m} value={m}>{m ? `Tell after ${m} min` : 'Off'}</MenuItem>)}
          </TextField>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2">When the room is locked</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Watched only while nobody is inside. Who hears a door opening or motion, and with which sound, is the 🔒 column of the alerts.
          </Typography>
          <Button size="small" variant="outlined" onClick={() => setAlertsOpen(true)}>Who hears it…</Button>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Ready-made lights</Typography>
          {!catalog && <CircularProgress size={18} />}
          {catalog && lights.length === 0 && (
            <Typography variant="body2" color="text.secondary">No lights are mapped to this room yet — map them, then come back.</Typography>
          )}
          {catalog && lights.length > 0 && (
            <>
              <TextField select size="small" fullWidth label="Light" value={light} onChange={(e) => setLight(e.target.value)} sx={{ mb: 1.5 }}>
                {lights.map((t) => (
                  <MenuItem key={`${t.UniqueNumber}/${t.Output}`} value={`${t.UniqueNumber}/${t.Output}`}>
                    {`${t.OutputName || `Output ${t.Output}`} — ${t.DeviceName}`}
                  </MenuItem>
                ))}
              </TextField>
              {READY.map((r) => (
                <Box key={r.kind} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ width: 0, flex: '1 1 0', minWidth: 160 }}>
                    <Typography variant="body2" fontWeight={700}>{r.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.text}</Typography>
                    {r.kind === 'night' && (
                      <Box>
                        <FormControlLabel control={<Checkbox size="small" checked={nightDoor} onChange={(e) => setNightDoor(e.target.checked)} />} label={<Typography variant="caption">door</Typography>} />
                        <FormControlLabel control={<Checkbox size="small" checked={nightMotion} onChange={(e) => setNightMotion(e.target.checked)} />} label={<Typography variant="caption">motion</Typography>} />
                      </Box>
                    )}
                  </Box>
                  <Button size="small" variant="contained" disabled={busy || !light} onClick={() => addReady(r.kind)}>Add</Button>
                </Box>
              ))}
              <Button size="small" onClick={() => setAutoOpen(true)}>All automations…</Button>
            </>
          )}

          {done && <Alert severity="success" sx={{ mt: 1.5 }}>{done}</Alert>}
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>Close</Button>
          <Button variant="contained" onClick={save} disabled={busy}>{busy ? <CircularProgress size={18} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>
      {alertsOpen && <SecurityAlertsDialog open onClose={() => setAlertsOpen(false)} locationId={locationId} />}
      {autoOpen && <AutomationsDialog open onClose={() => setAutoOpen(false)} locationId={locationId} />}
    </>
  );
}
